import React, { useCallback, useEffect, useRef, useState } from 'react'
import { allPass } from 'ramda'
import { makeStyles } from '@material-ui/styles'

import { authorizeNodeHelpLink, bareOSHelpLink } from 'k8s/links'
import { capitalizeString, castBoolToStr } from 'utils/misc'

import ExternalLink from 'core/components/ExternalLink'
import { masterNodeLengthValidator } from 'core/utils/fieldValidators'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import ValidatedForm from 'core/components/validatedForm/ValidatedForm'
import FormReviewTable from 'core/components/validatedForm/review-table'
import WizardStep from 'core/components/wizard/WizardStep'

import ClusterNameField from '../../form-components/NameField'
import ClusterHostChooser from '../ClusterHostChooser'
import KubernetesVersionField from '../../form-components/KubernetesVersionField'

import Theme from 'core/themes/model'
import { ClusterCreateTypeNames, ClusterCreateTypes } from '../../model'
import { CalicoDetectionTypes } from '../../form-components/CalicoNetworkFields'
import { CloudProviders } from 'app/plugins/infrastructure/components/cloudProviders/model'
import { bareOSClusterTracking } from '../../tracking'

import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { listNodes } from 'app/plugins/infrastructure/components/nodes/new-actions'
import { nodesSelector } from 'app/plugins/infrastructure/components/nodes/selectors'

import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import {
  checkNodesForClockDrift,
  clockDriftErrorMessage,
  networkingStackFilter,
  isUnassignedNode,
  isConnected,
} from 'app/plugins/infrastructure/components/nodes/helpers'
import { compareVersions } from 'k8s/util/helpers'
import { oneClickEtcdBackupDefaults } from '../../cluster-addons/etcd-backup'

export const initialContext = {
  containersCidr: '10.20.0.0/22',
  servicesCidr: '10.21.0.0/22',
  networkPlugin: 'calico',
  calicoIpIpMode: 'Always',
  calicoNatOutgoing: true,
  calicoBlockSize: '26',
  runtimeConfigOption: 'default',
  mtuSize: 1440,
  privileged: true,
  etcdBackup: true,
  prometheusMonitoringEnabled: true,
  allowWorkloadsOnMaster: true,
  tags: [],
  networkStack: 'ipv4',
  calicoIPv4: 'autodetect',
  calicoIPv6: 'none',
  calicoDetectionMethod: CalicoDetectionTypes.FirstFound,
  useHostname: false,
  // enableProfileAgent: true,
  containerRuntime: 'containerd',
  ...oneClickEtcdBackupDefaults,
}

export const columns = [
  { id: 'containersCidr', label: 'Containers CIDR' },
  { id: 'servicesCidr', label: 'Services CIDR' },
  {
    id: 'networkPlugin',
    label: 'CNI',
    render: (value) => capitalizeString(value),
    insertDivider: true,
  },
  { id: 'calicoIpIpMode', label: 'IP in IP Encapsulation Mode' },
  { id: 'calicoNatOutgoing', label: 'NAT Outgoing', render: (value) => castBoolToStr()(value) },
  { id: 'calicoBlockSize', label: 'Block Size' },
  { id: 'mtuSize', label: 'MTU Size' },
  { id: 'containerRuntime', label: 'Container Runtime' },
  {
    id: 'privileged',
    label: 'Privileged',
    render: (value) => castBoolToStr()(value),
    insertDivider: true,
  },
  {
    id: 'allowWorkloadsOnMaster',
    label: 'Enable workloads on master node',
    render: (value) => castBoolToStr()(value),
  },
  {
    id: 'etcdBackup',
    label: 'ETCD Backup',
    render: (value) => castBoolToStr()(value),
    insertDivider: true,
  },
  { id: 'etcdStoragePath', label: 'Storage Path' },
  { id: 'etcdBackupTimestamp', label: 'Daily Backup Time' },
  { id: 'maxTimestampBackups', label: 'Max Timestamp Backup Count' },
  // {
  //   id: 'enableProfileAgent',
  //   label: 'Enable profile agent',
  //   render: (value) => castBoolToStr()(value),
  // },
]

const trackingFields = {
  platform: CloudProviders.PhysicalMachine,
  target: ClusterCreateTypes.OneClick,
}

const OneClickPhysicalMachineCluster = ({ wizardContext, setWizardContext, onNext, ...rest }) => {
  const classes = useStyles({})
  const [errorMessage, setErrorMessage] = useState('')
  useListAction(listNodes)
  const allNodes = useSelector(nodesSelector)
  const validatorRef = useRef(null)

  const setupValidator = (validate) => {
    validatorRef.current = { validate }
  }
  const submitStep = useCallback(async () => {
    const isValid = await validatorRef.current.validate()
    if (!isValid) {
      return false
    }

    const selectedNodeUuids = wizardContext.masterNodes
    const hasClockDrift = checkNodesForClockDrift(selectedNodeUuids, allNodes)
    if (hasClockDrift) {
      setErrorMessage(clockDriftErrorMessage)
      return false
    }
    return true
  }, [wizardContext])

  // useEffect(() => {
  //   if (compareVersions(wizardContext.kubeRoleVersion, '1.20') >= 0) {
  //     setWizardContext({ enableProfileAgent: true })
  //   } else {
  //     setWizardContext({ enableProfileAgent: false })
  //   }
  // }, [wizardContext.kubeRoleVersion])

  useEffect(() => {
    onNext(submitStep)
  }, [submitStep])

  return (
    <WizardStep
      stepId="physical-one-click"
      onNext={bareOSClusterTracking.oneClick(trackingFields)}
      {...rest}
    >
      <ValidatedForm
        classes={{ root: classes.validatedFormContainer }}
        fullWidth
        initialValues={wizardContext}
        onSubmit={setWizardContext}
        triggerSubmit={setupValidator}
        elevated={false}
      >
        {/* <PollingData loading={loading} onReload={reload} hidden /> */}
        {/* Cluster Name */}
        <FormFieldSection
          title={`${ClusterCreateTypeNames[ClusterCreateTypes.OneClick]} Single Node Cluster Setup`}
          link={
            <ExternalLink textVariant="caption2" url={bareOSHelpLink}>
              BareOS Cluster Help
            </ExternalLink>
          }
        >
          <ClusterNameField setWizardContext={setWizardContext} />
          <KubernetesVersionField
            wizardContext={wizardContext}
            setWizardContext={setWizardContext}
            inClusterCreation={true}
          />
        </FormFieldSection>

        <FormFieldSection
          title="Select a node"
          link={
            <ExternalLink textVariant="caption2" url={authorizeNodeHelpLink}>
              Not Seeing Any Nodes?
            </ExternalLink>
          }
        >
          <ClusterHostChooser
            id="masterNodes"
            selection="single"
            filterFn={allPass([
              isConnected,
              isUnassignedNode,
              networkingStackFilter(wizardContext.networkStack),
            ])}
            onChange={(value) => setWizardContext({ masterNodes: value })}
            validations={[masterNodeLengthValidator]}
            isSingleNodeCluster
            required
          />
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </FormFieldSection>

        <FormFieldSection title="Default Settings for New Cluster">
          <FormReviewTable data={wizardContext} columns={columns} />
        </FormFieldSection>
      </ValidatedForm>
    </WizardStep>
  )
}

export default OneClickPhysicalMachineCluster

const useStyles = makeStyles<Theme>((theme) => ({
  validatedFormContainer: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
}))
